import {
  useCreateCommentMutation,
  GetActivityFeedDocument,
  GetActivityFeedQuery,
  GetActivityFeedQueryVariables,
  CommentInfoFragment,
  CommentArtboardInfoFragment,
  CommentArtboardInfoFragmentDoc,
  CreateCommentMutation,
  UserFragment,
  useGetInitialUserQuery,
  GetDetailViewArtboardDocument,
  GetShareDocument,
} from 'gqlTypes'
import uuid from 'uuid/v4'
import produce from 'immer'
import { DataProxy } from 'apollo-cache'
import { ErrorHandler } from 'modules/tracing'
import { dataIdFromObject } from 'graphql/apolloClient'
import { ArtboardInSidebar, ShareInSidebar } from 'modules/shares/types'
import { useApolloClient } from '@apollo/react-hooks'
import { ApolloClient } from 'apollo-client'

/**
 * Types
 */
interface CreateCommentProps {
  permanentArtboardShortId: ArtboardInSidebar['permanentArtboardShortId']
  documentVersionShortId: ArtboardInSidebar['documentVersionShortId']
  artboardIdentifier: ArtboardInSidebar['identifier']
  commentIdentifier: string | undefined
  shareIdentifier: ShareInSidebar['identifier']
}

interface OptimisticResponseProps {
  permanentArtboardShortId: string
  documentVersionShortId: string
  commentIdentifier?: string
  body: string
  user: UserFragment
  client: ApolloClient<any>
}

interface CreateCommentArgs {
  body: string
}

/**
 * Helpers
 */
const generateOptimisticResponse = ({
  permanentArtboardShortId,
  documentVersionShortId,
  commentIdentifier,
  body,
  user,
  client,
}: OptimisticResponseProps): CreateCommentMutation => {
  const artboard = client.readFragment<CommentArtboardInfoFragment>({
    fragment: CommentArtboardInfoFragmentDoc,
    fragmentName: 'CommentArtboardInfo',
    id: dataIdFromObject({
      __typename: 'Artboard',
      permanentArtboardShortId,
      documentVersionShortId,
    }) as string,
  })

  const updatedArtboard = produce(artboard, artboard => {
    if (artboard?.artboardBase) {
      const unreadCount = artboard.artboardBase.unreadCount ?? 0
      artboard.artboardBase.unreadCount = unreadCount + 1
      artboard.artboardBase.commentCount++
    }
  })

  return {
    __typename: 'RootMutationType',
    createComment: {
      __typename: 'CommentPayload',
      successful: true,
      errors: [],
      comment: {
        __typename: 'Comment',
        identifier: uuid(),
        body,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        isEdited: false,
        user: {
          __typename: 'PublicUser',
          identifier: user?.identifier!,
          name: user?.name,
          avatar: {
            __typename: 'Avatar',
            small: user?.avatar?.small!,
          },
        },
        parent: commentIdentifier
          ? {
              __typename: 'ParentComment',
              identifier: commentIdentifier,
              subscriptionStatus: 'OFF',
            }
          : null,
        userIsCreator: true,
        userCanDelete: false,
        isOptimistic: true,
        isRead: true,
        artboard: updatedArtboard,
        replies: [],
        subscriptionStatus: 'OFF',
      },
    },
  }
}

/**
 * Hook to create a new comment
 *
 * Note: this hook uses an optimistic response approach, that implies that
 * `update` will trigger twice, once with the optimistic response and then
 * another one with the response from the server
 */
export const useCreateComment = ({
  permanentArtboardShortId,
  documentVersionShortId,
  artboardIdentifier,
  commentIdentifier,
  shareIdentifier,
}: CreateCommentProps) => {
  const { data } = useGetInitialUserQuery()
  const client = useApolloClient()

  const getActivityFeed = (cache: DataProxy) => {
    return cache.readQuery<GetActivityFeedQuery, GetActivityFeedQueryVariables>(
      {
        query: GetActivityFeedDocument,
        variables: { permanentArtboardShortId, shareIdentifier },
      }
    )?.activityFeed
  }

  const updateActivityListWithComment = (
    activities: GetActivityFeedQuery['activityFeed'],
    comment: CommentInfoFragment
  ) => {
    // New reply if commentIdentifier exists
    if (commentIdentifier) {
      return produce(activities, draftState => {
        const commentToUpdate = draftState?.entries.find(
          activity => activity?.identifier === commentIdentifier
        ) as CommentInfoFragment

        commentToUpdate.replies.unshift({
          ...comment,
          isOptimistic: !!comment?.isOptimistic,
        } as CommentInfoFragment)

        commentToUpdate.subscriptionStatus = 'ON'
      })
    }

    // New comment if commentIdentifier doesn't exist
    return produce(activities, draftState => {
      draftState?.entries.unshift({
        ...comment,
        isOptimistic: !!comment?.isOptimistic,
      } as CommentInfoFragment)
    })
  }

  const [mutate, ...rest] = useCreateCommentMutation({
    redirectErrors: true,
    onError: 'show-toast',
  })

  const mutateAugmented = async ({ body }: CreateCommentArgs) => {
    if (!data?.me) {
      const message = 'There should be always an user while creating a comment'
      ErrorHandler.shouldNeverHappen(message)
      throw new Error(message)
    }

    return await mutate({
      variables: {
        body,
        // these conditions here are just to make it clearer, that we
        // expect to have only one of these two arguments at once.
        artboardIdentifier: commentIdentifier ? undefined : artboardIdentifier,
        commentIdentifier: commentIdentifier ? commentIdentifier : undefined,
      },
      optimisticResponse: generateOptimisticResponse({
        permanentArtboardShortId,
        documentVersionShortId,
        body,
        user: data.me!,
        commentIdentifier,
        client,
      }),
      update: (cache, { data }) => {
        if (!data?.createComment.successful) return

        const comment = data.createComment.comment!
        const activities = getActivityFeed(cache)
        const queryFieldName = 'activityFeed'

        cache.writeQuery({
          query: GetActivityFeedDocument,
          variables: { permanentArtboardShortId, shareIdentifier },
          data: {
            [queryFieldName]: updateActivityListWithComment(
              activities as GetActivityFeedQuery['activityFeed'],
              comment as CommentInfoFragment
            ),
          },
        })
      },
      // Refetch share and artboard to update subscription status
      refetchQueries: ({ data }) => {
        if (!data?.createComment?.successful) return []

        return [
          {
            query: GetShareDocument,
            variables: { shortId: shareIdentifier },
          },
          {
            query: GetDetailViewArtboardDocument,
            variables: {
              permanentArtboardShortId,
              documentVersionShortId,
              shareIdentifier,
            },
          },
        ]
      },
    })
  }

  return [mutateAugmented, ...rest] as const
}
